import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

const DISMISS_UNTIL_KEY = 'pwa_install_prompt_dismiss_until'
const INSTALLED_KEY = 'pwa_install_prompt_installed'
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000

const isStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent)

const isMobileDevice = () =>
  /android|iphone|ipad|ipod/i.test(window.navigator.userAgent)

const getIosBrowser = () => {
  const ua = window.navigator.userAgent || ''
  if (/CriOS/i.test(ua)) return 'chrome'
  if (/FxiOS/i.test(ua)) return 'firefox'
  if (/EdgiOS/i.test(ua)) return 'edge'
  if (/Safari/i.test(ua)) return 'safari'
  return 'other'
}

// iOS Safari Share button icon (box with arrow up)
const IosShareIcon = () => (
  <IosShareIconSvg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="5" y="10" width="14" height="10" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 10V4M9 7l3-3 3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </IosShareIconSvg>
)

const PwaInstallPrompt = ({ enabled = true }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [visible, setVisible] = useState(false)
  const [guideOpen, setGuideOpen] = useState(false)
  const [iosBrowser, setIosBrowser] = useState('other')

  const platform = useMemo(() => {
    if (isIos()) return 'ios'
    if (/android/i.test(window.navigator.userAgent)) return 'android'
    return 'other'
  }, [])

  useEffect(() => {
    if (!enabled) {
      setVisible(false)
      setGuideOpen(false)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return
    if (localStorage.getItem(INSTALLED_KEY) === '1') return
    if (!isMobileDevice() || isStandaloneMode()) return
    const dismissUntil = Number(localStorage.getItem(DISMISS_UNTIL_KEY) || 0)
    if (dismissUntil && Date.now() < dismissUntil) return

    // iOS: no beforeinstallprompt API; show step-by-step instructions
    if (isIos()) {
      setVisible(true)
      return
    }

    const handler = event => {
      event.preventDefault()
      setDeferredPrompt(event)
      setVisible(true)
    }

    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [enabled])

  useEffect(() => {
    const markInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, '1')
      setVisible(false)
    }

    // Chromium browsers fire this when PWA is installed.
    window.addEventListener('appinstalled', markInstalled)

    // If opened in standalone mode, consider app installed for this browser profile.
    if (isStandaloneMode()) {
      markInstalled()
    }

    return () => window.removeEventListener('appinstalled', markInstalled)
  }, [])

  useEffect(() => {
    if (isIos()) {
      setIosBrowser(getIosBrowser())
    }
  }, [])

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_UNTIL_KEY, String(Date.now() + DISMISS_TTL_MS))
    setVisible(false)
  }

  const handleInstall = async () => {
    if (platform === 'ios') {
      setGuideOpen(true)
      return
    }

    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const choice = await deferredPrompt.userChoice
    if (choice?.outcome) {
      localStorage.setItem(DISMISS_UNTIL_KEY, String(Date.now() + DISMISS_TTL_MS))
    }
    setDeferredPrompt(null)
    setVisible(false)
  }

  if (!visible || isStandaloneMode()) return null

  const iosGuideSteps = iosBrowser === 'safari'
    ? [
        'Tap the Share button.',
        'Tap "Add to Home Screen".',
        'Tap "Add" in the top right.'
      ]
    : iosBrowser === 'chrome'
      ? [
          'Tap the menu button (•••).',
          'Tap "Share".',
          'Tap "More", then choose "Add to Home Screen".',
          'Tap "Add".'
        ]
    : [
        'Open this page in Safari.',
        'In Safari, tap the Share button.',
        'Select "Add to Home Screen", then tap "Add".'
      ]

  return (
    <>
      <PromptCard role="dialog" aria-label="Install app" $isIos={platform === 'ios'}>
      <PromptTitle>Install Building Passport</PromptTitle>
      {platform === 'ios' ? (
        <PromptText>Use the install guide for your browser.</PromptText>
      ) : (
        <PromptText>Install the app for faster access and offline support.</PromptText>
      )}
      <Actions>
        <SecondaryButton type="button" onClick={handleDismiss}>Not now</SecondaryButton>
        <PrimaryButton type="button" onClick={handleInstall}>
          {platform === 'ios' ? 'Install guide' : 'Install'}
        </PrimaryButton>
      </Actions>
      </PromptCard>

      {platform === 'ios' && guideOpen && (
        <GuideOverlay onClick={() => setGuideOpen(false)}>
          <GuideCard onClick={event => event.stopPropagation()} role="dialog" aria-label="iOS install guide">
            <GuideTitle>
              {iosBrowser === 'safari' ? 'Install in Safari' : iosBrowser === 'chrome' ? 'Install in Chrome (iPhone)' : 'Install on iPhone'}
            </GuideTitle>
            <IosInstructions>
              {iosGuideSteps.map((step, index) => (
                <IosStep key={step}>
                  <StepNum>{index + 1}</StepNum>
                  <StepText>
                    {index === 0 && iosBrowser === 'safari'
                      ? (<><ShareButtonHint><IosShareIcon /> Share</ShareButtonHint> in Safari.</>)
                      : index === 1 && iosBrowser === 'chrome'
                        ? (<><ShareButtonHint><IosShareIcon /> Share</ShareButtonHint>.</>)
                      : step}
                  </StepText>
                </IosStep>
              ))}
            </IosInstructions>
            <GuideActions>
              <PrimaryButton type="button" onClick={() => setGuideOpen(false)}>Done</PrimaryButton>
            </GuideActions>
          </GuideCard>
        </GuideOverlay>
      )}
    </>
  )
}

export default PwaInstallPrompt

const PromptCard = styled.div`
  position: fixed;
  left: 12px;
  right: 12px;
  /* iOS keeps its own offset so the card stays clear of Safari's share bar,
     which the instructions point at. */
  bottom: ${({ $isIos }) =>
    $isIos ? 'calc(env(safe-area-inset-bottom, 0px) + 72px)' : 'calc(var(--bp-float-1) + var(--bp-stack-height, 0px))'};
  z-index: 100000;
  background: #0c1229;
  color: #fff;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
`

const PromptTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
`

const PromptText = styled.div`
  font-size: 12px;
  opacity: 0.9;
`

const IosInstructions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 4px 0 0;
`

const IosStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const StepNum = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #33becc;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StepText = styled.span`
  font-size: 12px;
  opacity: 0.95;
  line-height: 1.4;
`

const ShareButtonHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  line-height: 1;
  vertical-align: middle;
`

const IosShareIconSvg = styled.svg`
  width: 14px;
  height: 14px;
  display: block;
  flex-shrink: 0;
`

const Actions = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const SecondaryButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 8px;
  background: transparent;
  color: #fff;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
`

const PrimaryButton = styled.button`
  border: 1px solid #33becc;
  border-radius: 8px;
  background: #33becc;
  color: #fff;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`

const GuideOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 100001;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`

const GuideCard = styled.div`
  width: min(560px, calc(100% - 16px));
  background: #fff;
  border-radius: 14px 14px 0 0;
  padding: 14px;
  color: #0c1229;
`

const GuideTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
`

const GuideActions = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
`
